import { omit } from 'ramda';
import type { Location, NodeEntry, PathRef } from 'slate';
import { Editor, Node, Path, Text, Transforms } from 'slate';

import { linkColor } from '../../constants';
import { inToTwips } from '../../util';
import type {
  AlineText,
  AlineTextFormats,
  BookmarkStartElement,
  EditingMode,
  InsertBookmarkEdit,
  LinkElement,
  ParagraphChildElement,
  ParagraphElement,
  SuggestedInstructionFieldEdit,
  TabElement,
  TabOptions,
  TOCElement,
  TocStyle,
} from '../internal';
import {
  AlineTransforms,
  bookmarkStartType,
  createBookmarkEnd,
  createBookmarkStart,
  createDefaultParagraph,
  createDefaultText,
  createDefaultToc,
  createLinkWithChildren,
  createParagraphWithChildren,
  createTabElement,
  EditingMode as EditingModeEnum,
  generateSuggestedEditGroupId,
  isBookmarkStartElement,
  isDocumentElement,
  isLinkElement,
  isParagraphElement,
  isTabElement,
  isTOCElement,
  Queries,
  TOC_STYLES,
} from '../internal';

const HEADING_LEVELS = ['1', '2', '3'] as const;
const EMPTY_TOC_TEXT = 'There are no headings in this document';

type HeadingLevel = (typeof HEADING_LEVELS)[number];

interface PendingHeadingBookmark {
  bookmarkName: string;
  headingPath: Path;
  markerId: number;
}

interface TocLinkElement extends LinkElement {
  headingLevel: HeadingLevel | null;
}

const TocTransforms = {
  insertToc(
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      tocStyle: TocStyle;
      at?: Location;
    }
  ) {
    const { at = editor.selection, authorId, editingMode, tocStyle } = options;
    if (!at) return;

    AlineTransforms.withoutNormalizing(editor, () => {
      const insertAt = getTocInsertLocation(editor, at);
      if (!insertAt) return;

      const suggestionGroupId =
        editingMode === EditingModeEnum.SUGGESTING ? generateSuggestedEditGroupId() : undefined;
      const { pendingBookmarks, tocNode } = getUpdatedToc(editor, {
        at: insertAt,
        requestedTocStyle: tocStyle,
        tocNode: createTocNode({ authorId, editingMode, suggestionGroupId, tocStyle }),
      });
      const headingPathRefs = pendingBookmarks.map(pendingBookmark =>
        Editor.pathRef(editor, pendingBookmark.headingPath)
      );

      const insertedNodes = AlineTransforms.insertContentNodes(editor, {
        at: insertAt,
        authorId,
        editingMode,
        nodes: [tocNode, ...getTocSpacerParagraphs(editor, { at: insertAt, insertAt })],
        processEdits: false,
        select: 'start',
        suggestionGroupId,
      });

      if (insertedNodes?.length) {
        applyPendingHeadingBookmarks(editor, pendingBookmarks, {
          authorId,
          editingMode,
          headingPathRefs,
          suggestionGroupId,
        });
        return;
      }

      for (const headingPathRef of headingPathRefs) headingPathRef.unref();
    });
  },

  updateToc(editor: Editor, tocEntry: NodeEntry<TOCElement>, requestedTocStyle?: TocStyle) {
    const [tocNode, tocCMPath] = tocEntry;

    AlineTransforms.withoutNormalizing(editor, () => {
      const { pendingBookmarks, tocNode: updatedTocNode } = getUpdatedToc(editor, {
        at: tocCMPath,
        requestedTocStyle,
        tocNode,
      });
      applyPendingHeadingBookmarks(editor, pendingBookmarks);
      updateTocNode(editor, {
        at: tocCMPath,
        tocNode: updatedTocNode,
      });
    });
  },
};

function getTocInsertLocation(editor: Editor, at: Location): Path | null {
  const start = Editor.start(editor, at);

  const documentEntry = Editor.above(editor, { at: start, match: isDocumentElement });
  if (!documentEntry) return null;

  const tocEntry = Editor.above(editor, {
    at: start,
    match: isTOCElement,
    mode: 'highest',
  });
  if (tocEntry) return Path.next(tocEntry[1]);

  const topLevelIndex = Editor.path(editor, start)[documentEntry[1].length];
  if (topLevelIndex === undefined) return null;

  return [...documentEntry[1], topLevelIndex];
}

function getTocSpacerParagraphs(
  editor: Editor,
  { at, insertAt }: { at: Location; insertAt: Location }
): ParagraphElement[] {
  if (!Path.isPath(insertAt) || !Node.has(editor, insertAt)) return [];

  const [blockBelow] = Editor.node(editor, insertAt);
  if (isParagraphElement(blockBelow)) return [];

  const textProps = getNormalStylesOnFontOf(editor, Queries.getFirstTextFormats(editor, { at }));

  return [createDefaultParagraph({ textProps })];
}

function getUpdatedToc(
  editor: Editor,
  {
    at,
    requestedTocStyle,
    tocNode,
  }: {
    at: Location;
    tocNode: TOCElement;
    requestedTocStyle?: TocStyle;
  }
): { pendingBookmarks: PendingHeadingBookmark[]; tocNode: TOCElement } {
  const headingEntries = Queries.getHeadings(editor);
  const capitalizeByLevel = getCapitalizeByLevel(tocNode);
  const tocLinks: TocLinkElement[] = [];
  const pendingBookmarks: PendingHeadingBookmark[] = [];
  const existingTab = getExistingTab(tocNode);
  const tocStyle = requestedTocStyle ?? getTocStyle(tocNode);
  const defaultTabOptions = {
    ...createTocTabOptions(editor, tocStyle),
    ...existingTab?.options,
  };
  let nextMarkerId = Queries.getNextMarkerId(editor, bookmarkStartType);

  for (const [heading, headingPath] of headingEntries) {
    const text = Node.string(heading);
    if (!text) continue;

    const textStyles = getTextStyles(editor, heading.variant);
    const pageNumber =
      Queries.getPageNumberForCMPoint(editor, Editor.start(editor, headingPath)) || 1;
    const headingLevel = getHeadingLevel(heading.variant);

    const bookmarkStart = getBookmarkStart(heading);
    if (bookmarkStart) {
      const existingLink = getExistingLink(tocNode, bookmarkStart.name);
      if (existingLink) {
        tocLinks.push(
          getUpdatedLinkElement(existingLink, {
            defaultTabOptions,
            headingLevel,
            pageNumber,
            text,
            textStyles,
            tocStyle,
          })
        );
      } else {
        tocLinks.push(
          createLinkNode({
            bookmarkName: bookmarkStart.name,
            capitalizeByLevel,
            defaultTabOptions,
            headingLevel,
            pageNumber,
            text,
            textStyles,
            tocStyle,
          })
        );
      }
    } else {
      const bookmarkName = `_Toc${nextMarkerId}`;

      tocLinks.push(
        createLinkNode({
          bookmarkName,
          capitalizeByLevel,
          defaultTabOptions,
          headingLevel,
          pageNumber,
          text,
          textStyles,
          tocStyle,
        })
      );
      pendingBookmarks.push({ bookmarkName, headingPath, markerId: nextMarkerId });
      nextMarkerId += 1;
    }
  }

  return {
    pendingBookmarks,
    tocNode: {
      ...tocNode,
      children: getTocChildren(editor, {
        at,
        defaultTabOptions,
        preserveStyleTab: Boolean(existingTab) || requestedTocStyle !== undefined,
        tocLinks,
        tocNode,
        tocStyle,
      }),
    },
  };
}

function getTocStyle(tocNode: TOCElement): TocStyle {
  const entryLink = getFirstEntryLink(tocNode);

  if (!entryLink) {
    if (tocNode.options.pageNumbersEntryLevelsRange) return TOC_STYLES.links;

    return TOC_STYLES.dotted;
  }

  const tab = entryLink.children.find((node): node is TabElement => isTabElement(node));
  if (!tab) return TOC_STYLES.links;

  if (tab.options?.leader === 'dot') return TOC_STYLES.dotted;

  return TOC_STYLES.plain;
}

function getTocParagraphs(tocNode: TOCElement): ParagraphElement[] {
  return tocNode.children.filter((child): child is ParagraphElement => isParagraphElement(child));
}

function getFirstEntryLink(tocNode: TOCElement): LinkElement | null {
  for (const paragraph of getTocParagraphs(tocNode)) {
    const link = paragraph.children.find((child): child is LinkElement => isLinkElement(child));
    if (link) return link;
  }

  return null;
}

function getExistingTab(tocNode: TOCElement): TabElement | null {
  for (const paragraph of getTocParagraphs(tocNode)) {
    for (const child of paragraph.children) {
      if (isTabElement(child)) return child;

      if (isLinkElement(child)) {
        const tab = child.children.find((node): node is TabElement => isTabElement(node));
        if (tab) return tab;
      }
    }
  }

  return null;
}

function createTocTabOptions(editor: Editor, tocStyle: TocStyle): TabOptions {
  const pageDimensions = Queries.activePageDimensions(editor, 'in');
  return {
    leader: tocStyle === TOC_STYLES.dotted ? 'dot' : 'none',
    position: inToTwips(
      pageDimensions.width - pageDimensions.marginLeft - pageDimensions.marginRight
    ),
    type: 'right',
  };
}

function createTocNode(options: {
  authorId: string;
  editingMode: EditingMode;
  tocStyle: TocStyle;
  suggestionGroupId?: string;
}): TOCElement {
  const { authorId, editingMode, suggestionGroupId, tocStyle } = options;
  const toc = createDefaultToc(tocStyle);
  if (editingMode !== EditingModeEnum.SUGGESTING) return toc;

  const insertEdit: SuggestedInstructionFieldEdit = {
    authorId,
    ...(suggestionGroupId ? { groupId: suggestionGroupId } : {}),
    instruction: 'TOC',
    type: 'insertInstructionField',
    value: {},
  };

  return { ...toc, suggestedEdits: [insertEdit] };
}

function isHeadingLevel(level: string): level is HeadingLevel {
  return HEADING_LEVELS.includes(level as HeadingLevel);
}

function getHeadingLevel(variant: ParagraphElement['variant']): HeadingLevel | null {
  const match = String(variant).match(/^(?:style-heading|TOC)([1-9])$/);
  const level = match?.at(1);
  return level && isHeadingLevel(level) ? level : null;
}

function getTextStyles(editor: Editor, variant: ParagraphElement['variant']) {
  const styleSheet = Queries.activeStyleSheet(editor);
  const headingLevel = getHeadingLevel(variant);
  if (headingLevel === null) return styleSheet['style-normal'].styles;

  const tocStyle = styleSheet[`TOC${headingLevel}`];
  return tocStyle?.styles ?? styleSheet['style-normal'].styles;
}

function getLinkTitleText(link: LinkElement): string {
  const parts: string[] = [];
  for (const child of link.children) {
    if (Text.isText(child) && child.text.trim() !== '') {
      parts.push(child.text);
    } else if (isTabElement(child)) {
      break;
    }
  }
  return parts.join('');
}

/**
 * From the existing TOC, determine which heading levels use all-uppercase text
 */
function getCapitalizeByLevel(tocNode: TOCElement): Set<HeadingLevel> {
  const capitalizeByLevel = new Set<HeadingLevel>();
  for (const child of tocNode.children) {
    if (isParagraphElement(child)) {
      const level = getHeadingLevel(child.variant);
      if (level && !capitalizeByLevel.has(level)) {
        const link = child.children.find((c): c is LinkElement => isLinkElement(c));
        if (link) {
          const titleText = getLinkTitleText(link);
          if (titleText.length > 0 && titleText === titleText.toUpperCase())
            capitalizeByLevel.add(level);
        }
      }
    }
  }

  return capitalizeByLevel;
}

function getBookmarkStart(heading: ParagraphElement): BookmarkStartElement | undefined {
  return heading.children.find((child: ParagraphChildElement) => isBookmarkStartElement(child));
}

function getExistingLink(tocNode: TOCElement, bookmarkName: string): LinkElement | null {
  for (const paragraph of getTocParagraphs(tocNode)) {
    const link = paragraph.children.find(
      (child): child is LinkElement => isLinkElement(child) && child.href === `#${bookmarkName}`
    );
    if (link) return link;
  }

  return null;
}

function getParagraphByLinkHref(tocNode: TOCElement, href: string): ParagraphElement | null {
  return (
    getTocParagraphs(tocNode).find(paragraph =>
      paragraph.children.some(
        (child: ParagraphChildElement) => isLinkElement(child) && child.href === href
      )
    ) ?? null
  );
}

function getParagraphByVariant(tocNode: TOCElement, variant: string): ParagraphElement | null {
  return getTocParagraphs(tocNode).find(paragraph => paragraph.variant === variant) ?? null;
}

function applyPendingHeadingBookmarks(
  editor: Editor,
  pendingBookmarks: PendingHeadingBookmark[],
  options: {
    authorId?: string;
    editingMode?: EditingMode;
    headingPathRefs?: PathRef[];
    suggestionGroupId?: string;
  } = {}
) {
  const { authorId, editingMode, headingPathRefs, suggestionGroupId } = options;
  for (const [index, pendingBookmark] of pendingBookmarks.entries()) {
    const headingPathRef = headingPathRefs?.at(index);
    const headingPath = headingPathRef ? headingPathRef.unref() : pendingBookmark.headingPath;
    if (headingPath)
      addBookmarkToHeading(
        editor,
        { ...pendingBookmark, headingPath },
        { authorId, editingMode, suggestionGroupId }
      );
  }
}

function addBookmarkToHeading(
  editor: Editor,
  { bookmarkName, headingPath, markerId }: PendingHeadingBookmark,
  {
    authorId,
    editingMode,
    suggestionGroupId,
  }: {
    authorId?: string;
    editingMode?: EditingMode;
    suggestionGroupId?: string;
  } = {}
) {
  const heading = Node.get(editor, headingPath);
  if (!isParagraphElement(heading)) return;

  const bookmarkStart = createBookmarkStart(markerId, bookmarkName);
  const bookmarkEnd = createBookmarkEnd(markerId);

  if (authorId && editingMode === EditingModeEnum.SUGGESTING) {
    const insertEdit: InsertBookmarkEdit = {
      authorId,
      ...(suggestionGroupId ? { groupId: suggestionGroupId } : {}),
      type: 'insertBookmark',
      value: {},
    };

    bookmarkStart.suggestedEdits = [insertEdit];
    bookmarkEnd.suggestedEdits = [insertEdit];
  }

  Transforms.insertNodes(editor, bookmarkStart, {
    at: [...headingPath, 0],
  });
  Transforms.insertNodes(editor, bookmarkEnd, {
    at: [...headingPath, heading.children.length + 1],
  });
}

function getTocChildren(
  editor: Editor,
  {
    at,
    defaultTabOptions,
    preserveStyleTab,
    tocLinks,
    tocNode,
    tocStyle,
  }: {
    at: Location;
    defaultTabOptions: TabOptions;
    preserveStyleTab: boolean;
    tocLinks: TocLinkElement[];
    tocNode: TOCElement;
    tocStyle: TocStyle;
  }
) {
  if (tocLinks.length === 0)
    return [
      createEmptyTocParagraph(editor, {
        at,
        defaultTabOptions,
        preserveStyleTab,
        tocNode,
        tocStyle,
      }),
    ];

  const [document] = Queries.getDocument(editor);
  const defaultTabStopIn = Queries.activeDefaultTabStopIn(document);

  return tocLinks.map(link => {
    const { headingLevel } = link;
    const existingParagraph =
      getParagraphByLinkHref(tocNode, link.href) ??
      (headingLevel ? getParagraphByVariant(tocNode, `TOC${headingLevel}`) : null);

    return createParagraphWithChildren({
      children: [omit(['headingLevel'], link)],
      paraProps: {
        ...omit(['children'], existingParagraph ?? {}),
        indentationLeft: getTocIndentationLeft(headingLevel, defaultTabStopIn),
        variant: headingLevel ? `TOC${headingLevel}` : 'style-normal',
      },
    });
  });
}

function getTocIndentationLeft(
  headingLevel: HeadingLevel | null,
  defaultTabStopIn: number
): number {
  if (!headingLevel) return 0;
  return (Number(headingLevel) - 1) * defaultTabStopIn;
}

function createEmptyTocParagraph(
  editor: Editor,
  {
    at,
    defaultTabOptions,
    preserveStyleTab,
    tocNode,
    tocStyle,
  }: {
    at: Location;
    defaultTabOptions: TabOptions;
    preserveStyleTab: boolean;
    tocNode: TOCElement;
    tocStyle: TocStyle;
  }
): ParagraphElement {
  const textStyles = getEmptyTocTextStyles(editor, { at, tocNode });
  const existingParagraph = getTocParagraphs(tocNode).at(0);
  const children: ParagraphChildElement[] = [createDefaultText(textStyles, EMPTY_TOC_TEXT)];
  if (
    preserveStyleTab &&
    tocStyle !== TOC_STYLES.links &&
    (!defaultTabOptions.leader || defaultTabOptions.leader === 'none')
  )
    children.push(createTabElement(textStyles, defaultTabOptions));

  return createParagraphWithChildren({
    children,
    paraProps: existingParagraph ? omit(['children'], existingParagraph) : undefined,
  });
}

function getEmptyTocTextStyles(
  editor: Editor,
  { at, tocNode }: { at: Location; tocNode: TOCElement }
): AlineTextFormats {
  const entryTitle = getFirstEntryLink(tocNode)?.children.find(Text.isText);
  const styledText =
    entryTitle && entryTitle.text.trim() !== ''
      ? entryTitle
      : Queries.getFirstTextFormats(editor, { at });

  return getNormalStylesOnFontOf(editor, styledText);
}

function getNormalStylesOnFontOf(
  editor: Editor,
  styledText: Partial<Pick<AlineTextFormats, 'fontSize' | 'fontType'>> | null
): AlineTextFormats {
  const normalStyles = Queries.activeStyleSheet(editor)['style-normal'].styles;
  if (!styledText) return normalStyles;

  return {
    ...normalStyles,
    fontSize: styledText.fontSize ?? normalStyles.fontSize,
    fontType: styledText.fontType ?? normalStyles.fontType,
  };
}

function updateTocNode(
  editor: Editor,
  {
    at,
    tocNode,
  }: {
    at: Path;
    tocNode: TOCElement;
  }
) {
  Transforms.removeNodes(editor, { at });
  Transforms.insertNodes(editor, tocNode, { at });
}

/**
 * Replace the text node with the updated text
 */
function getUpdatedText(
  textNode: AlineText | TabElement | undefined | null,
  {
    existingText,
    text,
    textStyles,
  }: {
    existingText: string;
    text: string;
    textStyles: AlineTextFormats;
  }
) {
  const updatedText =
    existingText.trim() !== '' && existingText === existingText.toUpperCase()
      ? text.toUpperCase()
      : text;
  if (textNode && Text.isText(textNode)) return { ...textNode, text: updatedText };
  return createDefaultText(textStyles, updatedText);
}

function getUpdatedTabElement(
  tabNode: AlineText | TabElement | undefined | null,
  textStyles: AlineTextFormats,
  defaultTabOptions: TabOptions
): TabElement {
  return tabNode && isTabElement(tabNode)
    ? {
        ...tabNode,
        children: [createDefaultText(textStyles || tabNode.children[0])],
        options: { ...defaultTabOptions, ...tabNode.options },
      }
    : createTabElement(textStyles, defaultTabOptions);
}

function getUpdatedPageNumberText(textStyles: AlineTextFormats, pageNumber: number) {
  return createDefaultText(textStyles, pageNumber.toString());
}

function getUpdatedLinkElement(
  existingLink: LinkElement,
  {
    defaultTabOptions,
    headingLevel,
    pageNumber,
    text,
    textStyles,
    tocStyle,
  }: {
    defaultTabOptions: TabOptions;
    headingLevel: HeadingLevel | null;
    pageNumber: number;
    text: string;
    textStyles: AlineTextFormats;
    tocStyle: TocStyle;
  }
): TocLinkElement {
  const { children } = existingLink;
  const textNode = children.find(
    (child): child is AlineText => Text.isText(child) && child.text.trim() !== ''
  );
  const textElements: AlineText[] = [];
  for (const child of children) {
    if (Text.isText(child) && child.text.trim() !== '') {
      textElements.push(child);
    } else if (isTabElement(child)) {
      break;
    }
  }

  const existingText = textElements.map(textElement => textElement.text).join('');
  const tabNode = children.find((child): child is TabElement => isTabElement(child));
  const updatedTextNode = applyTocTitleStyle(
    getUpdatedText(textNode, { existingText, text, textStyles }),
    { headingLevel, textStyles, tocStyle }
  );

  if (tocStyle === TOC_STYLES.links)
    return { ...existingLink, children: [updatedTextNode], headingLevel };

  const updatedTabNode = getUpdatedTabElement(tabNode, textStyles, defaultTabOptions);
  const updatedPageNumberNode = getUpdatedPageNumberText(textStyles, pageNumber);

  return {
    ...existingLink,
    children: [updatedTextNode, updatedTabNode, updatedPageNumberNode],
    headingLevel,
  };
}

function createLinkNode({
  bookmarkName,
  capitalizeByLevel,
  defaultTabOptions,
  headingLevel,
  pageNumber,
  text,
  textStyles,
  tocStyle,
}: {
  bookmarkName: string;
  capitalizeByLevel: Set<HeadingLevel>;
  headingLevel: HeadingLevel | null;
  pageNumber: number;
  text: string;
  textStyles: AlineTextFormats;
  tocStyle: TocStyle;
  defaultTabOptions?: TabOptions;
}): TocLinkElement {
  const updatedText =
    headingLevel && capitalizeByLevel.has(headingLevel) ? text.toUpperCase() : text;
  const titleNode = applyTocTitleStyle(createDefaultText(textStyles, updatedText), {
    headingLevel,
    textStyles,
    tocStyle,
  });
  const children: LinkElement['children'] = [titleNode];

  if (tocStyle !== TOC_STYLES.links) {
    children.push(
      createTabElement(textStyles, defaultTabOptions),
      createDefaultText(textStyles, pageNumber.toString())
    );
  }

  return {
    ...createLinkWithChildren(`#${bookmarkName}`, children),
    headingLevel,
  };
}

function applyTocTitleStyle(
  textNode: AlineText,
  {
    headingLevel,
    textStyles,
    tocStyle,
  }: {
    headingLevel: HeadingLevel | null;
    textStyles: AlineTextFormats;
    tocStyle: TocStyle;
  }
): AlineText {
  if (tocStyle === TOC_STYLES.links) {
    return {
      ...textNode,
      fontColor: { color: linkColor, origin: 'USER_APPLIED' },
      underline: { active: true, origin: 'USER_APPLIED' },
    };
  }

  return {
    ...textNode,
    bold: headingLevel === '1' ? { active: true, origin: 'USER_APPLIED' } : textStyles.bold,
    fontColor: textStyles.fontColor,
    underline: textStyles.underline,
  };
}

export default TocTransforms;
